import { ReactElement, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '../../controls/Button';
import CheckboxControl from '../../controls/Checkbox';
import { Alert } from '../../constants/telosair-interfaces';
import { useData } from '../../context/data.context';
import { updateAlert } from '../../services/dashboard-service';
import { TOAST_MESSAGES } from '../../constants/global-constants';

type AssignUsersProps = {
  alert: Alert;
};

/**
 * "Assigned to" on an alert: shows who is working it and lets anyone in the
 * organisation change that. The server checks every assignee belongs to the
 * caller's client and logs the change in the alert's activity.
 * @param props - The alert.
 * @returns The assignee chips and the picker.
 */
export default function AssignUsers(props: AssignUsersProps): ReactElement {
  const { alert } = props;
  const { clientUsers, updateAlertData } = useData();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(alert.assignedTo ?? []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(alert.assignedTo ?? []);
  }, [alert.id, alert.assignedTo]);

  const assigned = alert.assignedTo ?? [];
  /**
   * Display name for a user login, falling back to the login itself.
   * @param userName - The user's login.
   * @returns Full name, or the login.
   */
  const nameOf = (userName: string): string => {
    const user = clientUsers.find((u) => u.userName?.toLowerCase() === userName.toLowerCase());
    const full = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '';
    return full || userName;
  };

  /**
   * Adds or removes one user from the pending selection.
   * @param userName - The user's login.
   */
  const toggle = (userName: string): void => {
    setSelected((prev) =>
      prev.includes(userName) ? prev.filter((u) => u !== userName) : [...prev, userName]
    );
  };

  /** Saves the selection. */
  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const response = await updateAlert({ assignedTo: selected }, alert.id);
      updateAlertData(response.data);
      toast.success(selected.length ? 'Alert assigned' : 'Alert unassigned');
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || TOAST_MESSAGES.error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-2">
        {assigned.map((userName) => (
          <span
            key={userName}
            title={userName}
            className="inline-flex items-center rounded bg-gray-800 px-2 py-1 text-xs text-primary-200"
          >
            {nameOf(userName)}
          </span>
        ))}
        <Button
          theme="secondary-color"
          size="sm"
          name={assigned.length ? 'Change' : 'Assign Users'}
          onClick={() => {
            setSelected(assigned);
            setOpen((prev) => !prev);
          }}
        />
      </div>
      {open && (
        <div className="absolute left-0 z-20 mt-2 w-72 rounded-lg border border-gray-700 bg-container-700 p-3 shadow-lg">
          <div className="mb-2 text-xs text-gray-400">Who is working on this alert?</div>
          <div className="max-h-60 overflow-auto">
            {clientUsers.length === 0 && (
              <div className="py-2 text-xs text-gray-500">No users in your organisation yet.</div>
            )}
            {clientUsers.map((user) => (
              <div key={user.id} className="flex items-center gap-2 py-1">
                <CheckboxControl
                  id={`assign-${alert.id}-${user.id}`}
                  checked={selected.includes(user.userName)}
                  onChange={() => toggle(user.userName)}
                  hideMarginLeft
                />
                <button
                  type="button"
                  className="min-w-0 text-left"
                  onClick={() => toggle(user.userName)}
                >
                  <div className="truncate text-sm text-gray-300">{nameOf(user.userName)}</div>
                  <div className="truncate text-xs text-gray-500">{user.userName}</div>
                </button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button theme="secondary-gray" size="sm" name="Cancel" onClick={() => setOpen(false)} />
            <Button theme="primary" size="sm" name="Save" loading={saving} onClick={save} />
          </div>
        </div>
      )}
    </div>
  );
}
